import { NgFor, NgIf } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NbButtonModule, NbCardModule, NbDialogService, NbIconModule, NbRouteTabsetModule } from '@nebular/theme';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { UtilityService } from 'src/app/core/services/utility.service';
import { RouterState } from 'src/app/features/auth/redux/router-state';
import { Drb } from 'src/app/models/drb-models/drb';
import { SourceImage } from 'src/app/models/drb-models/source-image';
import { SourceItem } from 'src/app/models/drb-models/source-item';
import { Xliff } from 'src/app/models/drb-models/xliff';
import { ACTIONS, DRB_ID_TYPE, MODE } from 'src/app/shared/constants/general-constants';
import { NgxGenericModalComponent } from 'src/app/shared/ui/ngx-generic-modal/ngx-generic-modal.component';
import { SpinnerState } from 'src/app/shared/ui/spinner/redux/spinner.state';
import { ENVIRONMENT } from 'src/environments/environment';
import { SharedModule } from '../../../shared/shared.module';
import { DeleteDrbComponent } from '../delete-drb/delete-drb.component';
import { EditImageComponent } from '../drb-sources/source-images/edit-image/edit-image.component';
import { ViewImageComponent } from '../drb-sources/source-images/view-image/view-image.component';
import { EditSourceItemComponent } from '../drb-sources/source-item/edit-source-item/edit-source-item.component';
import { ViewSourceItemComponent } from '../drb-sources/source-item/view-source-item/view-source-item.component';
import { EditXliffComponent } from '../drb-xliff/edit-xliff/edit-xliff.component';
import { ViewXliffComponent } from '../drb-xliff/view-xliff/view-xliff.component';
import { DrbState, DrbStateModel, LoadDrb, LoadDrbByVersion, LoadDrbVersions, SaveDrbState } from '../redux';

@Component({
  selector: 'app-view-drb',
  templateUrl: '../drb-sources/drb-sources.component.html',
  styleUrls: ['../drb-sources/drb-sources.component.css'],
  imports: [
    NgIf,
    SharedModule,
    NbCardModule,
    NgSelectComponent,
    FormsModule,
    NgFor,
    NgOptionComponent,
    NbButtonModule,
    NbIconModule,
    NbRouteTabsetModule
  ]
})
export class ViewDrbComponent implements OnInit, OnDestroy {
  public marketing$: Observable<Drb> = inject(Store).select(DrbState.marketing);
  public marketing_translations$: Observable<Drb> = inject(Store).select(DrbState.marketing_translations);
  public version$: Observable<any> = inject(Store).select(DrbState.versions);
  public translating$: Observable<boolean> = inject(Store).select(DrbState.translating);
  public loading$: Observable<boolean> = inject(Store).select(SpinnerState.isLoading);
  public username$: Observable<string> = inject(Store).select(RouterState.username);
  private unsubscribe = new Subject<void>();

  public spin;
  public mode = MODE.VIEW;
  public drb;
  public username;
  public versions;
  public version;
  public environment: string = ENVIRONMENT.name;
  public bundle_name: string;
  public isDraft;

  public strings;
  public files;
  public images;
  public links;
  public videos;
  public showActions = [];
  public translating;
  public deploying = false;

  constructor(
    private route: ActivatedRoute,
    private store: Store,
    private dialogService: NbDialogService,
    public utility: UtilityService
  ) {}

  public ngOnInit() {
    this.version$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (this.bundle_name && data[this.bundle_name].length > 0) {
        this.versions = [...data[this.bundle_name]].reverse();
        if (!this.store.selectSnapshot(DrbState.getByKey(this.bundle_name as keyof DrbStateModel))) {
          this.store.dispatch(new LoadDrbByVersion(this.versions[0], this.bundle_name));
        }
      }
    });
    this.translating$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.translating = data));
    this.loading$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.spin = data));
    this.username$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.username = data));
    this.marketing$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data && data.version) {
        this.drb = data;
        this.version = data.version;
        this.strings = data.xliff.en;
        this.files = data.source.files;
        this.images = data.source.images;
        this.links = data.source.links;
        this.videos = data.source.videos;
      }
    });
    this.marketing_translations$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data && data.version) {
        this.drb = data;
        this.version = data.version;
        this.strings = data.xliff.en;
        this.files = data.source.files;
        this.images = data.source.images;
        this.links = data.source.links;
        this.videos = data.source.videos;
      }
    });
    this.route.url.subscribe((event) => {
      this.bundle_name = event[1].path;
      this.loadData();
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public loadData() {
    if (this.store.selectSnapshot(DrbState.versions)[this.bundle_name].length === 0) {
      this.store.dispatch(new LoadDrbVersions(this.bundle_name));
    } else {
      const drb: Drb = this.store.selectSnapshot(DrbState.getByKey(this.bundle_name as keyof DrbStateModel));
      this.drb = drb;
      this.version = drb.version;
      this.strings = drb.xliff.en;
      this.files = drb.source.files;
      this.images = drb.source.images;
      this.links = drb.source.links;
      this.videos = drb.source.videos;
    }
  }

  public onChange(version) {
    if (version) {
      this.store.dispatch(new LoadDrbByVersion(version, this.bundle_name));
    }
  }

  public reload() {}

  public manageContributors() {}

  public displayContributors() {
    if (this.drb['contributors']) {
      const contributors = JSON.parse(JSON.stringify(this.drb['contributors'])).filter((element) => element.length > 0);

      return contributors.join(', ');
    }
  }

  public optForTranslation() {
    const localDrb = JSON.parse(JSON.stringify(this.drb));

    localDrb.last_updated_by = this.username;
    localDrb.last_updated_ts = Math.floor(Date.now() / 1000);
    localDrb.translate = !this.drb.translate;
    localDrb.bundle_name = this.bundle_name;

    this.store.dispatch(new SaveDrbState(localDrb, this.bundle_name));
  }

  public getNextMonday(date = new Date()) {
    const dateCopy = new Date(date.getTime());
    let nextMonday;
    if (dateCopy.getHours() < 16) {
      nextMonday = new Date(dateCopy.setDate(dateCopy.getDate() + ((7 - dateCopy.getDay() + 1) % 7)));
    } else {
      nextMonday = new Date(dateCopy.setDate(dateCopy.getDate() + ((7 - dateCopy.getDay() + 1) % 7 || 7)));
    }

    return nextMonday.getFullYear() + '/' + (nextMonday.getMonth() + 1) + '/' + nextMonday.getDate();
  }

  public getTwoWeeksFromNow(timestamp: number) {
    const date = timestamp + 86400 * 14;

    return this.utility.convertUnixTime_global(date);
  }

  public clickedButton(type, $event) {
    if ($event.action === ACTIONS.REFRESH) {
      this.dialogService
        .open(NgxGenericModalComponent, {
          context: {
            title: $localize`Confirm Refresh`,
            message: $localize`Are you sure that you want to refresh your DRB version ${this.version}?`
          }
        })
        .onClose.subscribe((data) => {
          if (data) {
            this.strings = undefined;
            this.files = undefined;
            this.images = undefined;
            this.links = undefined;
            this.videos = undefined;
            this.store.dispatch(new LoadDrb(this.bundle_name));
          }
        });
    }
    if ($event.action === ACTIONS.CREATE) {
      switch (type) {
        case DRB_ID_TYPE.STRING:
          this.dialogService.open(EditXliffComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              action: $event.action,
              xliff: new Xliff(),
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.FILE:
          this.dialogService.open(EditSourceItemComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              action: $event.action,
              sourceItem: new SourceItem(),
              type: type,
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.IMAGE:
          this.dialogService.open(EditImageComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              action: $event.action,
              image: new SourceImage(),
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.LINK:
          this.dialogService.open(EditSourceItemComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              action: $event.action,
              sourceItem: new SourceItem(),
              type: type,
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.VIDEO:
          this.dialogService.open(EditSourceItemComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              action: $event.action,
              sourceItem: new SourceItem(),
              type: type,
              bundle_name: this.bundle_name
            }
          });
          break;
      }
    }
    if ($event.action === ACTIONS.VIEW) {
      switch (type) {
        case DRB_ID_TYPE.STRING:
          this.dialogService.open(ViewXliffComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              xliff: $event.data
            }
          });
          break;
        case DRB_ID_TYPE.FILE:
          this.dialogService.open(ViewSourceItemComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              sourceItem: $event.data,
              type: type
            }
          });
          break;
        case DRB_ID_TYPE.IMAGE:
          this.dialogService.open(ViewImageComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              image: JSON.parse(JSON.stringify($event.data))
            }
          });
          break;
        case DRB_ID_TYPE.LINK:
          this.dialogService.open(ViewSourceItemComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              sourceItem: $event.data,
              type: type
            }
          });
          break;
        case DRB_ID_TYPE.VIDEO:
          this.dialogService.open(ViewSourceItemComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              sourceItem: $event.data,
              type: type
            }
          });
          break;
      }
    }
    if ($event.action === ACTIONS.EDIT) {
      switch (type) {
        case DRB_ID_TYPE.STRING:
          this.dialogService.open(EditXliffComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              action: $event.action,
              xliff: JSON.parse(JSON.stringify($event.data)),
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.FILE:
          this.dialogService.open(EditSourceItemComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              action: $event.action,
              sourceItem: JSON.parse(JSON.stringify($event.data)),
              type: type,
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.IMAGE:
          this.dialogService.open(EditImageComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              action: $event.action,
              image: JSON.parse(JSON.stringify($event.data)),
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.LINK:
          this.dialogService.open(EditSourceItemComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              action: $event.action,
              sourceItem: JSON.parse(JSON.stringify($event.data)),
              type: type,
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.VIDEO:
          this.dialogService.open(EditSourceItemComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              action: $event.action,
              sourceItem: JSON.parse(JSON.stringify($event.data)),
              type: type,
              bundle_name: this.bundle_name
            }
          });
          break;
      }
    }
    if ($event.action === ACTIONS.DELETE) {
      switch (type) {
        case DRB_ID_TYPE.STRING:
          this.dialogService.open(DeleteDrbComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              item: JSON.parse(JSON.stringify($event.data)),
              type: type,
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.FILE:
          this.dialogService.open(DeleteDrbComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              item: JSON.parse(JSON.stringify($event.data)),
              type: type,
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.IMAGE:
          this.dialogService.open(DeleteDrbComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              item: JSON.parse(JSON.stringify($event.data)),
              type: type,
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.LINK:
          this.dialogService.open(DeleteDrbComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              item: JSON.parse(JSON.stringify($event.data)),
              type: type,
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.VIDEO:
          this.dialogService.open(DeleteDrbComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              item: JSON.parse(JSON.stringify($event.data)),
              type: type,
              bundle_name: this.bundle_name
            }
          });
          break;
      }
    }
  }
}
